import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import {
    Box,
    CircularProgress,
    Typography,
    Container,
    Button
} from "@mui/material";
import { apiInst, getINPaymentStatus } from "../hooks/apiHandler";
import { useNavigate } from "react-router-dom";

// 1. PayTest Component: Updated for Razorpay Subscriptions
export const PayTest = ({ onCloseTrigger }) => {
    const [loading, setLoading] = useState(false);
    const storeID = useRef(JSON.parse(window.localStorage.getItem("ds")) || "default_store");
    const navigate = useNavigate();

    const startSubscription = async () => {
        // Ensure Razorpay SDK is loaded
        if (!window.Razorpay) {
            return console.error("Razorpay SDK failed to load. Please add the checkout.js script to your index.html.");
        }

        setLoading(true);
        try {
            // STEP 1: Call Backend to create the Razorpay Subscription
            const res = await apiInst.post("/api/inpay/create-subscription", {
                storeID: storeID.current
            });

            // Expected response from the server code we wrote earlier
            const { subscription_id, key_id, name, email } = res.data;

            // STEP 2: Configure Razorpay Options
            const options = {
                key: key_id,
                subscription_id: subscription_id,
                name: "MiiRA POS",
                description: "Monthly Subscription",
                image: "https://www.miirapos.com/favicon.ico", // Optional
                config: {
                    display: {
                        blocks: {
                            banks: {
                                name: 'Pay using Card or UPI',
                                instruments: [
                                    { method: 'card' },
                                    { method: 'upi' }
                                ],
                            },
                        },
                        sequence: ['block.banks'],
                        preferences: {
                            show_default_blocks: true,
                        },
                    },
                },
                handler: function (response) {
                    // This function executes after successful payment
                    console.log("Payment Successful:", response);
                    const { razorpay_payment_id, razorpay_subscription_id, razorpay_signature } = response;
                    // Redirect to your status page
                    navigate(`/admin?session_id=${subscription_id}`);
                    if (onCloseTrigger) onCloseTrigger(subscription_id);
                    axios.post("/api/inpay/payResponse/" + subscription_id, {
                        razorpay_payment_id,
                        razorpay_subscription_id,
                        razorpay_signature,
                        storeID: storeID.current
                    }).then((verifyResp) => {
                        console.log("Subscription Verified:", verifyResp.data);
                    }).catch((err) => {
                        console.error("Verification Error:", err.response?.data || err.message);
                    });
                },
                prefill: {
                    name: name,
                    email: email,
                },
                theme: {
                    color: "#007bff"
                },
                modal: {
                    ondismiss: function () {
                        setLoading(false);
                        console.log("Checkout modal closed by user");
                    }
                }
            };

            // STEP 3: Open Razorpay Popup
            const rzp = new window.Razorpay(options);
            rzp.open();

        } catch (err) {
            console.error("Subscription Error:", err.response?.data || err.message);
            setLoading(false);
        }
    };

    useEffect(() => {
        startSubscription();
    }, []);

    return (
        <Box sx={{ textAlign: 'center', margin: 'auto', mt: 4, maxWidth: 400 }}>
            {loading ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                    <CircularProgress />
                    <Typography variant="body2" color="textSecondary">
                        Initializing secure payment...
                    </Typography>
                </Box>
            ) : (
                <Button
                    variant="contained"
                    fullWidth
                    onClick={startSubscription}
                    sx={{ py: 1.5, bgcolor: '#007bff', '&:hover': { bgcolor: '#0056b3' } }}
                >
                    Retry Payment
                </Button>
            )}
        </Box>
    );
};

// 2. ReturnInPayment Component: Verifies the Razorpay Sub Status
export const ReturnInPayment = ({ onCloseTrigger }) => {
    const [status, setStatus] = useState(null);
    const [, setCustomerEmail] = useState('');
    const supportEmail = "support@miirapos.com";

    useEffect(() => {
        const fetchPaymentStatus = async () => {
            const urlParams = new URLSearchParams(window.location.search);
            const subId = urlParams.get('session_id'); // We passed sub_id as session_id in the URL

            if (!subId) {
                setStatus('error');
                return;
            }

            try {
                // This calls your /get-sub-details endpoint
                const resp = await getINPaymentStatus({ subID: subId });
                if (resp && resp.info) {
                    // Razorpay status: created, authenticated, active, cancelled, etc.
                    setStatus(resp.info.status);
                    // Email might be in notes or fetched via customer object
                    setCustomerEmail(resp.info.notes?.email || "your registered email");
                } else {
                    throw new Error("Invalid response from server");
                }
            } catch (error) {
                console.error("Error fetching payment status:", error);
                setStatus('error');
            }
            
        };

        fetchPaymentStatus();
    }, []);

    if (status === null) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh', flexDirection: 'column' }}>
                <CircularProgress />
                <Typography sx={{ marginTop: 2 }}>Verifying subscription, please wait...</Typography>
            </Box>
        );
    }

    // Razorpay success state for subscriptions is typically 'active' or 'authenticated'
    if (status === 'active' || status === 'authenticated') {
        return (
            <Container maxWidth="sm" sx={{ textAlign: 'center', py: 8 }}>
                <Box sx={{ mb: 4 }}>
                    <Box sx={{
                        width: 80, height: 80, bgcolor: 'success.light',
                        borderRadius: '50%', display: 'flex', mx: 'auto',
                        alignItems: 'center', justifyContent: 'center', mb: 2
                    }}>
                        <Typography variant="h3" color="white">✓</Typography>
                    </Box>
                    <Typography variant="h4" gutterBottom>Subscription Active!</Typography>
                    <Typography variant="body1">
                        Thank you for subscribing. Your account is now being provisioned.
                    </Typography>
                    <Typography sx={{ mt: 2 }}>
                        Questions? Email <a href={`mailto:${supportEmail}`}>{supportEmail}</a>
                    </Typography>
                </Box>
                <Button variant="contained" onClick={onCloseTrigger} size="large">
                    Go to Dashboard
                </Button>
            </Container>
        );
    }

    return (
        <Container maxWidth="sm" sx={{ textAlign: 'center', py: 8 }}>
            <Typography variant="h5" color="error" gutterBottom>Payment Pending or Failed</Typography>
            <Typography variant="body1" sx={{ mb: 4 }}>
                Current Status: <strong>{status.toUpperCase()}</strong>.
                If you just paid, it may take a moment to update.
            </Typography>
            <Button variant="contained" onClick={onCloseTrigger}>
                Close
            </Button>
        </Container>
    );
};